import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  LinearProgress, Typography,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../../supabaseClient'
import { useAuth } from '../../../auth/AuthContext'
import { useViewerStudent } from '../../../auth/ViewAsContext'
import { Project } from '../../../types'
import StepRoadmap from '../../../components/student/StepRoadmap'
import StudentUpdatesPanel from '../../../components/student/StudentUpdatesPanel'
import WhatsNewCard from '../../../components/student/WhatsNewCard'
import FairProfileDialog from '../../../components/fair/FairProfileDialog'
import ImportantDatesCard from '../../../components/fair/ImportantDatesCard'

// Student dashboard. Two things matter here per the team's principle:
//   1. What is the current step? (hero card)
//   2. What is the full roadmap? (StepRoadmap)
// Everything else (class info, fair countdowns, promo copy) was removed —
// it lives in dedicated pages (e.g. /my-class) reachable from the sidebar.

const STEP_LABELS_SCIENCE: Record<number, string> = {
  1: 'Select a Research Topic',
  2: 'Ask a Research Question',
  3: 'Gather Background Information',
  4: 'Formulate a Hypothesis',
  5: 'Design an Experiment',
  6: 'Conduct the Experiment and Record Data',
  7: 'Analyze Data and Draw Conclusions',
  8: 'Present Your Findings',
}

const STEP_LABELS_ENGINEERING: Record<number, string> = {
  1: 'Select a Research Topic',
  2: 'Identify a Problem',
  3: 'Gather Background Information',
  4: 'Specify Design Requirements',
  5: 'Design a Solution',
  6: 'Develop the Solution',
  7: 'Test and Record Performance',
  8: 'Present Your Solutions',
}

const STEP_DESCRIPTIONS_SCIENCE: Record<number, string> = {
  1: 'Choose a phenomenon in the natural world you are curious about — a good topic is observable, measurable, and connected to a broader field of knowledge',
  2: 'Define the specific relationship you want to investigate — your question must identify the variables involved and be answerable through empirical evidence',
  3: 'Survey existing knowledge so your work builds on established science and does not duplicate what is already known',
  4: 'Make an evidence-based, testable prediction — state the expected relationship between your independent and dependent variables based on your research',
  5: 'Plan a controlled procedure that isolates variables and eliminates bias — a good experimental design is repeatable and produces measurable data',
  6: 'Execute your experiment exactly as designed and record observations and measurements systematically — accurate records are the foundation of scientific integrity',
  7: 'Examine your data for patterns and determine whether the hypothesis is supported and what the findings mean for the broader field',
  8: 'Share your methodology, data, and conclusions so others can evaluate, replicate, and build on your work',
}

const STEP_DESCRIPTIONS_ENGINEERING: Record<number, string> = {
  1: 'Identify a domain where human needs are unmet or existing solutions fall short — the topic defines the space in which the problem lives',
  2: 'Precisely define the problem, the end user, and the context — a well-defined problem is the most critical step, solving the wrong problem wastes every step that follows',
  3: 'Research existing solutions, relevant science, available materials, and known constraints — this prevents reinventing the wheel and informs smarter design decisions',
  4: 'Define what the solution must do and what constraints it must operate within — performance targets, cost, size, and safety become the measurable criteria for success',
  5: 'Translate requirements into a concrete plan — sketches, schematics, or specifications — multiple designs are often considered before selecting the most promising approach',
  6: 'Build the actual product, code, or system — this is where plans meet reality and unforeseen challenges emerge',
  7: 'Test the solution against the design requirements, record results objectively, and when performance falls short, identify root causes and iterate',
  8: 'Document the design process, test results, and recommendations — professional communication of engineering work is as important as the solution itself',
}

export default function StudentDashboard() {
  const { session, profile, studentLifecycle, trialDaysLeft } = useAuth()
  const { studentId: viewerStudentId, viewerIsStudent, studentName, prefixPath, loading: vasLoading } = useViewerStudent()
  const navigate = useNavigate()

  const [project,      setProject]      = useState<Project | null>(null)
  const [loading,      setLoading]      = useState(true)
  const [noProfile,    setNoProfile]    = useState(false)
  const [hasFairEntry, setHasFairEntry] = useState<boolean | null>(null)
  const [fairDismissed, setFairDismissed] = useState(false) // "decide later" — session only

  useEffect(() => {
    async function load() {
      if (!session || vasLoading) return
      if (!viewerStudentId) { setNoProfile(true); setLoading(false); return }

      const { data: projects } = await supabase
        .from('project')
        .select('*')
        .eq('student_id', viewerStudentId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
      const proj = projects?.[0] ?? null
      setProject(proj)
      if (proj) {
        const { data: entry } = await supabase
          .from('project_fair_entry')
          .select('id')
          .eq('project_id', proj.project_id)
          .maybeSingle()
        setHasFairEntry(!!entry)
      } else {
        setHasFairEntry(false)
      }
      setLoading(false)
    }
    load()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, viewerStudentId, vasLoading])

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
  if (noProfile) {
    return <Box sx={{ p: 4 }}><Typography>Setting up your profile...</Typography></Box>
  }

  const currentStep    = project?.current_step ?? 1
  const completedCount = currentStep - 1
  const totalSteps     = 8
  const progress       = (completedCount / totalSteps) * 100
  const firstName      = viewerIsStudent
                          ? (profile?.first_name?.trim() || null)
                          : (studentName?.split(' ')[0] || null)
  const lifecycleState = studentLifecycle?.lifecycle_state ?? null
  const isTrialActive  = lifecycleState === 'trial_active'
  const isTrialExpired = lifecycleState === 'trial_expired'
  // Only fall back to auth metadata for users who pre-date the lifecycle
  // model. Once a student has a lifecycle row, that's the source of truth —
  // otherwise enrolled students whose metadata still says is_trial=true
  // (legacy from signup) get the trial-only copy + Step 1 lock.
  const isTrial        = isTrialActive
                          || isTrialExpired
                          || (lifecycleState === null && session?.user.user_metadata?.is_trial === true)
  const isEngineering  = project?.track === 'engineering_design'
  const STEP_LABELS    = isEngineering ? STEP_LABELS_ENGINEERING       : STEP_LABELS_SCIENCE
  const STEP_DESCS     = isEngineering ? STEP_DESCRIPTIONS_ENGINEERING : STEP_DESCRIPTIONS_SCIENCE

  // Fair prompt: contextual, not a login-gate. Only nag once the project has
  // real shape (Step 3+) so trial exploration stays friction-free — and only
  // for the actual student (not a mentor in View-As), who hasn't picked a fair,
  // hasn't opted out, and hasn't dismissed for this session.
  const fairOptOut     = !!(project as (Project & { fair_opt_out?: boolean }) | null)?.fair_opt_out
  const showFairDialog = !!project && viewerIsStudent && !isTrial
                          && currentStep >= 3 && hasFairEntry === false
                          && !fairOptOut && !fairDismissed

  return (
    <Box sx={{ p: { xs: 3, sm: 4 }, maxWidth: 820 }}>

      {/* ── Trial countdown / expired banner ── */}
      {isTrialActive && trialDaysLeft !== null && (
        <Box sx={{
          mb: 3, px: 2, py: 1.5, borderRadius: 2,
          bgcolor: trialDaysLeft < 14 ? 'warning.lighter' : 'rgba(15,43,76,0.04)',
          border: '1px solid',
          borderColor: trialDaysLeft < 14 ? 'warning.light' : 'rgba(15,43,76,0.12)',
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Chip
            size="small"
            color={trialDaysLeft < 14 ? 'warning' : 'primary'}
            label={`Trial — ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} remaining`}
          />
          <Typography variant="caption" color="text.secondary">
            You have full access to Step 1 during the trial. Enroll to unlock the rest of your research.
          </Typography>
        </Box>
      )}
      {isTrialExpired && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Your 60-day trial has ended. Your existing work is preserved — contact your office to enroll and continue.
        </Alert>
      )}

      {/* ── Free trial how-to — short, not a gate. Suggests AI Lab first, then
          Step 1, but doesn't block either path. ── */}
      {isTrialActive && (
        <Card variant="outlined" sx={{ mb: 3, borderColor: 'primary.light', bgcolor: 'rgba(15,43,76,0.02)' }}>
          <CardContent sx={{ '&:last-child': { pb: 2 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
              How your free trial works
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
              <Chip label="1" size="small" color="primary" sx={{ fontWeight: 700, minWidth: 24 }} />
              <Typography variant="body2">
                Pick one project in the <strong>AI Lab</strong> to try AI-guided research firsthand — pick a dataset, ask a question, and see a result.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
              <Chip label="2" size="small" color="primary" sx={{ fontWeight: 700, minWidth: 24 }} />
              <Typography variant="body2">
                Then start <strong>Step 1</strong> below to create your own research topic.
              </Typography>
            </Box>
            <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate(prefixPath('/learn/ai-research-lab'))}>
              Go to the AI Lab
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── Updates panel — surfaces decided revision requests, etc. ── */}
      <StudentUpdatesPanel />

      {/* ── Fair important dates — reminder layer, self-hides if no fair entry ── */}
      {project && (
        <ImportantDatesCard
          projectId={project.project_id}
          canChange={viewerIsStudent}
        />
      )}

      {/* ── Fair selection prompt (Step 3+, real student, no fair yet) ── */}
      {project && (
        <FairProfileDialog
          projectId={project.project_id}
          open={showFairDialog}
          onClose={() => setFairDismissed(true)}
          onSaved={() => { setHasFairEntry(true); setFairDismissed(true) }}
        />
      )}

      {/* ── What's new — dismissible release notes ── */}
      <WhatsNewCard />

      {/* ── Greeting ── */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
          {firstName ? `Welcome back, ${firstName}.` : 'Welcome back.'}
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: '0.95rem' }}>
          {isTrial
            ? "You're exploring the program. Keep going — great research starts with curiosity."
            : completedCount === 0
              ? 'Your research journey starts with choosing the right direction. Step 1 is ready for you.'
              : completedCount < totalSteps
                ? `You've completed ${completedCount} of ${totalSteps} steps. Keep the momentum going.`
                : 'All steps complete. Your mentor will review and advance your project.'}
        </Typography>
      </Box>

      {/* ── Hero: current step card ── */}
      {currentStep <= totalSteps && (
        <Card
          sx={{
            mb:           3,
            bgcolor:      'primary.main',
            color:        '#fff',
            borderRadius: 3,
            cursor:       'pointer',
            '&:hover':    { bgcolor: 'primary.dark' },
            transition:   'background-color 0.15s',
          }}
          onClick={() => navigate(prefixPath(`/step/${currentStep}`))}
        >
          <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.7, mb: 0.5 }}>
                  Your next step
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                  Step {currentStep}: {STEP_LABELS[currentStep] ?? 'Continue'}
                </Typography>
                <Typography sx={{ opacity: 0.85, fontSize: '0.9rem', maxWidth: 480 }}>
                  {isTrial && currentStep === 1
                    ? 'As a trial student, start by watching the intro lessons, then experience AI-guided research by selecting your interest categories'
                    : STEP_DESCS[currentStep] ?? ''}
                </Typography>
              </Box>
              <Box sx={{
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
                bgcolor:        'rgba(255,255,255,0.15)',
                borderRadius:   '50%',
                width:          44,
                height:         44,
                flexShrink:     0,
                ml:             2,
              }}>
                <ArrowForwardIcon />
              </Box>
            </Box>

            {/* Mini progress inside hero */}
            <Box sx={{ mt: 2.5 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height:       6,
                  borderRadius: 3,
                  bgcolor:      'rgba(255,255,255,0.2)',
                  '& .MuiLinearProgress-bar': { bgcolor: '#fff', borderRadius: 3 },
                }}
              />
              <Typography sx={{ mt: 0.75, fontSize: '0.75rem', opacity: 0.7 }}>
                {completedCount} of {totalSteps} steps complete
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Research Roadmap — all 8 steps as a compact list. Current step
          is also rendered above as the hero card; here it appears as a
          row to preserve the linear journey ordering. Clicking an
          unlocked step navigates to /step/N. */}
      {project && (
        <StepRoadmap
          project={project}
          viewerIsStudent={viewerIsStudent}
          prefixPath={prefixPath}
          isTrial={isTrial}
        />
      )}
    </Box>
  )
}
